import React, { useState, useEffect } from "react"
import { useRecommendation } from "../contexts/RecommendationContext"

const RecipientSelector = ({ onRecipientSelect, selectedRecipient }) => {
  const { recipients, loadRecipients, saveRecipient, deleteRecipient, loading } = useRecommendation()
  const [showNewRecipientForm, setShowNewRecipientForm] = useState(false)
  const [newRecipient, setNewRecipient] = useState({
    name: "",
    age: "",
    gender: "",
    interests: "",
    relationship: "",
    notes: "",
  })

  useEffect(() => {
    loadRecipients()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSaveNewRecipient = async () => {
    const recipientData = {
      ...newRecipient,
      age: parseInt(newRecipient.age) || null,
      interests: newRecipient.interests
        .split(",")
        .map(i => i.trim())
        .filter(Boolean),
    }

    const result = await saveRecipient(recipientData)

    if (result.success) {
      // Clear form
      setNewRecipient({
        name: "",
        age: "",
        gender: "",
        interests: "",
        relationship: "",
        notes: "",
      })
      setShowNewRecipientForm(false)

      // Reload recipients to show the new one
      await loadRecipients()

      // Small delay to ensure state has updated
      setTimeout(() => {
        // Auto-select the new recipient
        onRecipientSelect(result.recipient)
      }, 100)
    } else {
      alert("Failed to save recipient: " + (result.error || "Unknown error"))
    }
  }

  const handleSelectRecipient = recipient => {
    onRecipientSelect(recipient)
  }

  const handleDeleteRecipient = async (recipientId, e) => {
    e.stopPropagation()
    if (window.confirm("Are you sure you want to delete this recipient?")) {
      const result = await deleteRecipient(recipientId)
      if (result.success) {
        // Reload recipients list
        await loadRecipients()
        // Clear selection if deleted recipient was selected
        if (selectedRecipient?.id === recipientId) {
          onRecipientSelect(null)
        }
      }
    }
  }

  return (
    <div className="mb-6">
      <label className="block text-sm font-medium text-gray-700 mb-2">Select Recipient</label>

      {/* Existing Recipients */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
        {recipients.map(recipient => (
          <div
            key={recipient.id}
            onClick={() => handleSelectRecipient(recipient)}
            className={`p-3 border rounded-lg cursor-pointer transition-colors relative group ${
              selectedRecipient?.id === recipient.id ? "border-purple-500 bg-purple-50" : "border-gray-200 hover:border-purple-300"
            }`}
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h4 className="font-medium text-gray-900">{recipient.name}</h4>
                <p className="text-sm text-gray-600">
                  {recipient.age && `${recipient.age} years old`}
                  {recipient.age && recipient.relationship && " • "}
                  {recipient.relationship}
                </p>
                {recipient.interests && recipient.interests.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Interests: {Array.isArray(recipient.interests) ? recipient.interests.join(", ") : recipient.interests}
                  </p>
                )}
              </div>
              <button
                onClick={e => handleDeleteRecipient(recipient.id, e)}
                className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 transition-opacity"
                title="Delete recipient"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        ))}

        {/* Add New Recipient Button */}
        <div
          onClick={() => setShowNewRecipientForm(true)}
          className="p-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-colors flex items-center justify-center"
        >
          <div className="text-center">
            <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <p className="text-sm text-gray-600">Add New Recipient</p>
          </div>
        </div>
      </div>

      {/* New Recipient Form */}
      {showNewRecipientForm && (
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <h4 className="font-medium text-gray-900 mb-3">Add New Recipient</h4>
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  required
                  value={newRecipient.name}
                  onChange={e => setNewRecipient({ ...newRecipient, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="e.g., Mom, John, Sarah"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Age</label>
                <input
                  type="number"
                  value={newRecipient.age}
                  onChange={e => setNewRecipient({ ...newRecipient, age: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="25"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Gender</label>
                <select
                  value={newRecipient.gender}
                  onChange={e => setNewRecipient({ ...newRecipient, gender: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="non-binary">Non-binary</option>
                  <option value="prefer-not-to-say">Prefer not to say</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Relationship</label>
                <select
                  value={newRecipient.relationship}
                  onChange={e => setNewRecipient({ ...newRecipient, relationship: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">Select relationship</option>
                  <option value="mother">Mother</option>
                  <option value="father">Father</option>
                  <option value="spouse">Spouse/Partner</option>
                  <option value="sibling">Sibling</option>
                  <option value="friend">Friend</option>
                  <option value="colleague">Colleague</option>
                  <option value="child">Child</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Interests</label>
              <input
                type="text"
                value={newRecipient.interests}
                onChange={e => setNewRecipient({ ...newRecipient, interests: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                placeholder="e.g., reading, cooking, technology (comma-separated)"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={newRecipient.notes}
                onChange={e => setNewRecipient({ ...newRecipient, notes: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                rows="2"
                placeholder="Any additional notes about their preferences..."
              />
            </div>

            <div className="flex justify-end space-x-2">
              <button type="button" onClick={() => setShowNewRecipientForm(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
                Cancel
              </button>
              <button
                type="button"
                disabled={loading || !newRecipient.name}
                onClick={handleSaveNewRecipient}
                className="px-4 py-2 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700 disabled:opacity-50"
              >
                {loading ? "Saving..." : "Save Recipient"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default RecipientSelector
